import {
  RATING_BAR_COLORS,
  SENTIMENT_ELEMENT_COLORS,
  SENTIMENT_THRESHOLDS,
  SentimentColorGroup,
} from 'features/charts-v2/constants'
import { METRICS } from 'features/charts-v2/types'

export type RidgelineMetric = 'sentiment' | 'rating'

export interface RidgelineBinSpec {
  /** The bin's category value as the backend emits it (integer bin index for
   *  sentiment, rounded star score for rating). */
  key: number
  /** Where the bin sits on the metric's axis. */
  x: number
  /** Human-readable bucket, e.g. "0.3 to 0.4" or "4 stars". */
  label: string
}

export const isRidgelineMetric = (metric: unknown): metric is RidgelineMetric =>
  metric === METRICS.SENTIMENT.id || metric === METRICS.RATING.id

// Sentiment bin i spans [i/10, (i+1)/10); a score of exactly 1.0 sits in bin 9.
const SENTIMENT_BIN_INDICES = Array.from({ length: 20 }, (_, i) => i - 10)
const SENTIMENT_BIN_WIDTH = 0.1
const STAR_SCORES = [1, 2, 3, 4, 5]

const formatSentimentEdge = (value: number): string =>
  (Math.round(value * 10) / 10).toFixed(1)

const SENTIMENT_BINS: readonly RidgelineBinSpec[] = SENTIMENT_BIN_INDICES.map(
  (index) => ({
    key: index,
    x: (2 * index + 1) / 20,
    label: `${formatSentimentEdge(index * SENTIMENT_BIN_WIDTH)} to ${formatSentimentEdge((index + 1) * SENTIMENT_BIN_WIDTH)}`,
  }),
)

const RATING_BINS: readonly RidgelineBinSpec[] = STAR_SCORES.map((star) => ({
  key: star,
  x: star,
  label: star === 1 ? '1 star' : `${star} stars`,
}))

/** The fixed, ordered bucket domain for a ridgeline metric. */
export const getRidgelineBins = (
  metric: RidgelineMetric,
): readonly RidgelineBinSpec[] =>
  metric === METRICS.SENTIMENT.id ? SENTIMENT_BINS : RATING_BINS

/** The bin spec for a bin key, or undefined when the metric has no such bin. */
export const findRidgelineBin = (
  metric: RidgelineMetric,
  key: number,
): RidgelineBinSpec | undefined =>
  getRidgelineBins(metric).find((bin) => bin.key === key)

/**
 * The score range a sentiment bin covers: `[min, max)`, or `[min, max]` for the
 * top bin. `k / 10` is the correctly rounded double for the decimal edge, so a
 * bin's edges equal the literals the backend compares against.
 */
export const getSentimentBinEdges = (
  key: number,
): { min: number; max: number } => ({ min: key / 10, max: (key + 1) / 10 })

/** One stretch of the x-axis that a ridge is drawn in a single colour over. */
export interface RidgelineColorBand {
  from: number
  to: number
  color: string
  /** The legend bucket this stretch belongs to (what legend hover matches on). */
  bucket: string
}

/**
 * The colour bands that tile a ridgeline's x-axis, from the same utility
 * colours and cut points as the rating / sentiment legend: a star band spans
 * `star ± 0.5` (the `getRatingBucket` cut points), and sentiment splits at
 * `SENTIMENT_THRESHOLDS`. The outer bands run out to the domain ends.
 */
export const getRidgelineColorBands = (
  metric: RidgelineMetric,
  [domainMin, domainMax]: [number, number],
): RidgelineColorBand[] => {
  if (metric === METRICS.SENTIMENT.id) {
    const edges = [
      domainMin,
      SENTIMENT_THRESHOLDS.NEGATIVE,
      SENTIMENT_THRESHOLDS.POSITIVE,
      domainMax,
    ]
    return [
      SentimentColorGroup.NEGATIVE,
      SentimentColorGroup.NEUTRAL,
      SentimentColorGroup.POSITIVE,
    ].map((group, index) => ({
      from: edges[index]!,
      to: edges[index + 1]!,
      color: SENTIMENT_ELEMENT_COLORS[group],
      bucket: group,
    }))
  }
  return STAR_SCORES.map((star) => ({
    from: star === STAR_SCORES[0] ? domainMin : star - 0.5,
    to: star === STAR_SCORES[STAR_SCORES.length - 1] ? domainMax : star + 0.5,
    color: RATING_BAR_COLORS[star]!,
    bucket: String(star),
  }))
}

/** Axis tick values for a ridgeline metric. */
export const RIDGELINE_AXIS_TICKS: Record<RidgelineMetric, number[]> = {
  sentiment: [-1, -0.5, 0, 0.5, 1],
  rating: [1, 2, 3, 4, 5],
}
